import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatCheckbox } from '@angular/material/checkbox';
import { Observable } from 'rxjs';
import { DrawingSetRevision } from '../../../../../../../../shared/models/drawing-sheet';
import { stationInputText } from '../../../../../../../../shared/helpers/design-context';
import { SHEET_TYPES } from '../../../../../../../../shared/helpers/sheet-types';

/** The panel's save line for a page (W46 item 7) */
export interface PageSaveState {
  /** `info`: a muted line that is not about a save ("2 left") */
  status: 'saving' | 'saved' | 'error' | 'info';
  text: string;
}

/** The set review the panel edits through: its commits, poll guard and hints, so a field behaves as everywhere else */
export interface SetPageHost {
  readonly editable: boolean;
  commit(row: DrawingSetRevision, field: 'number' | 'title' | 'label' | 'discipline', input: HTMLInputElement): void;
  commitStation(row: DrawingSetRevision, field: 'station_start' | 'station_end', input: HTMLInputElement): void;
  commitType(row: DrawingSetRevision, value: string): void;
  pickCandidate(row: DrawingSetRevision, sheetUuid: string | null): void;
  drop(row: DrawingSetRevision): void;
  editingStarted(row: DrawingSetRevision): void;
  editingEnded(row: DrawingSetRevision): void;
  duplicateNumber(row: DrawingSetRevision): boolean;
  /** W46: whether the page blocks the publish, and where it sits among the pages to fix ("Issue n of N") */
  needsAttention(row: DrawingSetRevision): boolean;
  issuePosition(row: DrawingSetRevision): { index: number; total: number } | null;
  /** Filtered to Needs attention: Previous / Next stop only on the pages still to fix */
  readonly filteredToProblems: boolean;
  walkable(row: DrawingSetRevision): boolean;
  /** "All issues fixed — ready to publish" once there is nothing left to step to while filtered */
  readonly problemsLeftText: string;
  /** Item 7: the page's save line, the field that failed, and when its saves have landed (false: one failed) */
  saveStatus(row: DrawingSetRevision): PageSaveState | null;
  saveFailed(row: DrawingSetRevision, field: string): boolean;
  whenSaved(row: DrawingSetRevision): Observable<boolean>;
  /** W48: Previous / Next (or PageUp / PageDown) moved the panel */
  pageStepped(direction: 'next' | 'prev'): void;
  /** Enter in the Number field: once saved, a page no longer to fix hands over to the next one */
  advanceWhenFixed(row: DrawingSetRevision): void;
  /** "Skip this page" */
  setSkip(row: DrawingSetRevision, skip: boolean): void;
  /** Review I4: drop a failed save, back to the saved values */
  discardFailedSave(row: DrawingSetRevision): void;
  stationPlaceholder(row: DrawingSetRevision, field: 'station_start' | 'station_end'): string;
  stationIsSuggestion(row: DrawingSetRevision, field: 'station_start' | 'station_end'): boolean;
  stationHintNote(row: DrawingSetRevision, field: 'station_start' | 'station_end'): string | null;
  labelPlaceholder(row: DrawingSetRevision): string;
  matchLabel(row: DrawingSetRevision): string;
  /** The panel shows this page: the list highlights it and moves to its list page */
  pageShown(row: DrawingSetRevision | null): void;
  /** The panel came up or went away */
  attachPanel(panel: DrawingSetPagePanelComponent | null): void;
  /** The full-page viewer (zoom and pan) */
  openViewer(row: DrawingSetRevision): void;
  /** PageUp / PageDown move the panel only from the panel or the review's list, and never under a dialog (I3) */
  pageKeysAllowed(target: EventTarget | null, inPanel: boolean): boolean;
}

export interface DrawingSetPagePanelData {
  host: SetPageHost;
  /** The pages to walk, read fresh each time: a poll or a drop replaces the set's revision array */
  rows: () => DrawingSetRevision[];
  /** The page shown first */
  uuid: string;
}

/**
 * Set review page panel (W42), in the app's record side panel (`projectsService.openRightPanel`, as an RFI or an
 * issue): the page preview on top (click for the full-page viewer), then every field, then Previous / Next (and
 * PageUp / PageDown) through the review's pages. Fields commit on blur, as everywhere; moving to another page blurs
 * the field first so a typed value is saved on its own page, and the next page gets fresh inputs.
 */
@Component({
  selector: 'app-drawing-set-page-panel',
  templateUrl: './drawing-set-page-panel.component.html',
  styleUrls: ['./drawing-set-page-panel.component.scss'],
  standalone: false,
})
export class DrawingSetPagePanelComponent implements OnInit, AfterViewInit, OnDestroy {
  /** Set by the right-panel host before the first change detection */
  data: DrawingSetPagePanelData;
  readonly sheetTypes = SHEET_TYPES;
  readonly stationText = stationInputText;
  uuid: string;
  private cdr = inject(ChangeDetectorRef);
  private host = inject(ElementRef<HTMLElement>);
  private shownFor: DrawingSetRevision | null = null;
  private shown: DrawingSetRevision[] = [];

  ngOnInit(): void {
    this.uuid = this.data?.uuid;
    this.data?.host.attachPanel(this);
    this.data?.host.pageShown(this.row);
  }

  /** Keyboard users land in the panel (I2): its heading takes focus once it is up */
  ngAfterViewInit(): void {
    (this.host.nativeElement.querySelector('.heading') as HTMLElement | null)?.focus({ preventScroll: true });
  }

  ngOnDestroy(): void {
    this.data?.host.attachPanel(null);
    this.data?.host.pageShown(null);
  }

  get rows(): DrawingSetRevision[] {
    return this.data?.rows() ?? [];
  }

  get index(): number {
    return this.rows.findIndex(r => r.uuid === this.uuid);
  }

  get row(): DrawingSetRevision | null {
    return this.rows[this.index] ?? null;
  }

  /** The page shown, as a one-item list the template keys on its uuid */
  get shownRows(): DrawingSetRevision[] {
    const row = this.row;
    if (row !== this.shownFor) {
      this.shownFor = row;
      this.shown = row ? [row] : [];
    }
    return this.shown;
  }

  get editable(): boolean {
    return !!this.data?.host.editable;
  }

  @ViewChild('skipBox') private skipBox?: MatCheckbox;
  /** Bumped by "Discard change": the page's fields are drawn again from the saved row */
  private resetKey = 0;
  resetKeys = [0];

  /** A failed skip save: the checkbox shows the row's saved state again (review I4) */
  resyncSkip(row: DrawingSetRevision): void {
    if (row.uuid !== this.uuid) {
      return;
    }
    if (this.skipBox) {
      this.skipBox.checked = !!row.skip_publish;
      this.cdr.markForCheck();
    } else {
      this.resetKeys = [++this.resetKey];
    }
  }

  /** "Discard change" (review I4): the failed field goes back to its saved value and the page is free to leave */
  discardChange(row: DrawingSetRevision): void {
    this.data.host.discardFailedSave(row);
    this.resetKeys = [++this.resetKey];
    this.cdr.detectChanges();
  }

  /** Waiting for a save before moving on (item 7) */
  moving = false;

  get canPrev(): boolean {
    return !!this.target(-1);
  }

  get canNext(): boolean {
    return !!this.target(1);
  }

  /** The save line: the page's save, else (filtered, nowhere left to go) how many issues are left */
  get status(): PageSaveState | null {
    const row = this.row;
    const save = row ? this.data.host.saveStatus(row) : null;
    if (save) {
      // A failed save says so under its field
      return save.status === 'error' ? null : save;
    }
    const left = this.data?.host.problemsLeftText;
    return left ? { status: left.startsWith('All') ? 'saved' : 'info', text: left } : null;
  }

  prev(): void {
    this.go(-1);
  }

  next(): void {
    this.go(1);
  }

  /** Another row clicked in the list while the panel is open: show it here, without reopening the panel */
  show(uuid: string): void {
    this.blurField();
    this.uuid = uuid;
    this.data.host.pageShown(this.row);
    this.cdr.markForCheck();
  }

  /**
   * PageUp / PageDown walk the pages from the panel or the review's list (I3). Text fields keep the keys, and so does
   * the page anywhere else, or under the full-page viewer.
   */
  @HostListener('document:keydown', ['$event'])
  onKey(event: KeyboardEvent): void {
    if (event.key !== 'PageDown' && event.key !== 'PageUp') {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (target?.closest?.('input, textarea, select, [contenteditable="true"]')) {
      return;
    }
    const inPanel = !!target && this.host.nativeElement.contains(target);
    if (!this.data?.host.pageKeysAllowed(target, inPanel)) {
      return;
    }
    event.preventDefault();
    this.go(event.key === 'PageDown' ? 1 : -1);
  }

  /** Enter saves the field (its blur) and leaves focus in the panel, where PageUp / PageDown move on */
  enterField(input: HTMLInputElement): void {
    input.blur();
    (this.host.nativeElement.querySelector('.wrapper') as HTMLElement | null)?.focus({ preventScroll: true });
  }

  /** Enter in the Number field (W46): saves it, then on to the next page to fix if this one is fixed now */
  enterNumber(row: DrawingSetRevision, input: HTMLInputElement): void {
    this.data.host.advanceWhenFixed(row);
    this.enterField(input);
  }

  /** "Issue 2 of 8" on a page to fix; null on any other page */
  get issue(): { index: number; total: number } | null {
    const row = this.row;
    return row ? this.data.host.issuePosition(row) : null;
  }

  drop(row: DrawingSetRevision): void {
    // The next page takes its place; the last page steps back
    const after = this.rows[this.index + 1] ?? this.rows[this.index - 1] ?? null;
    this.data.host.drop(row);
    this.uuid = after?.uuid ?? null;
    this.data.host.pageShown(after);
  }

  /** The next page Previous / Next stop on: every page, or (filtered) the next one still to fix */
  private target(step: number): DrawingSetRevision | null {
    const rows = this.rows;
    if (this.index < 0) {
      return null;
    }
    for (let i = this.index + step; i >= 0 && i < rows.length; i += step) {
      if (this.data.host.walkable(rows[i])) {
        return rows[i];
      }
    }
    return null;
  }

  /**
   * Commits the field being typed in (its blur saves it), then moves once the page's saves have landed. A failed save
   * keeps the page open with its error, so a change is never lost by moving on (item 7).
   */
  private go(step: number): void {
    const row = this.row;
    if (!row || this.moving) {
      return;
    }
    const hadFocus = this.host.nativeElement.contains(document.activeElement);
    this.blurField();
    this.moving = true;
    this.data.host.whenSaved(row).subscribe(saved => {
      this.moving = false;
      // Still on that page (a row click meanwhile wins), and its saves landed
      if (saved && this.uuid === row.uuid) {
        this.moveTo(step, hadFocus);
      }
      this.cdr.markForCheck();
    });
  }

  private moveTo(step: number, hadFocus: boolean): void {
    const target = this.target(step);
    if (!target) {
      return;
    }
    this.uuid = target.uuid;
    this.data.host.pageShown(target);
    this.data.host.pageStepped(step > 0 ? 'next' : 'prev');
    this.cdr.detectChanges();
    // The old page's heading or field went with it: keep focus in the panel so the keys go on working
    if (hadFocus && !this.host.nativeElement.contains(document.activeElement)) {
      (this.host.nativeElement.querySelector('.wrapper') as HTMLElement | null)?.focus({ preventScroll: true });
    }
  }

  /** Blurs the page's field being typed in, which saves it */
  private blurField(): void {
    const active = document.activeElement as HTMLElement | null;
    if (active && this.host.nativeElement.contains(active) && active.matches('input, select, textarea')) {
      active.blur();
    }
  }
}
